import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import API_BASE_URL from "../../../config/api";
import { FaEdit, FaTrash, FaPlus, FaFileUpload, FaCloudUploadAlt, FaFileExcel, FaUserTie } from "react-icons/fa";

const AddExpertContainer = () => {
    const [experts, setExperts] = useState([]);
    const [formData, setFormData] = useState({
        industryExpertId: "",
        name: "",
        email: "",
        industry: "",
    });
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [uploadedExperts, setUploadedExperts] = useState(null);
    const [uploadError, setUploadError] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalExperts, setTotalExperts] = useState(0);
    const limit = 10;

    const formRef = useRef(null);

    useEffect(() => {
        fetchExperts(currentPage);
    }, [currentPage]);

    const fetchExperts = async (page) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/experts?page=${page}&limit=${limit}`);
            // Handle new paginated response structure
            if (response.data.experts) {
                setExperts(response.data.experts);
                setTotalPages(response.data.totalPages || 1);
                setTotalExperts(response.data.totalExperts || 0);
            } else {
                // Fallback for flat array if backend reverts
                setExperts(response.data);
            }
        } catch (error) {
            toast.error("Failed to fetch experts");
            console.error("Error fetching experts:", error);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });
    };

    const resetForm = () => {
        setFormData({
            industryExpertId: "",
            name: "",
            email: "",
            industry: "",
        });
        setIsEditing(false);
        setEditId(null);
    };

    const validateForm = () => {
        const { industryExpertId, name, email, industry } = formData;
        if (!industryExpertId || !name || !email || !industry) {
            toast.error("All fields are required");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            if (isEditing) {
                await axios.put(`${API_BASE_URL}/api/expert/${editId}`, formData);
                toast.success("Expert updated successfully");
            } else {
                await axios.post(`${API_BASE_URL}/api/addexpert`, formData);
                toast.success("Expert added successfully");
            }
            resetForm();
            fetchExperts(currentPage);
        } catch (error) {
            const message = error.response?.data?.message || "Operation failed";
            toast.error(message);
            console.error("Error:", error);
        }
    };

    const handleEdit = (expert) => {
        setFormData({
            industryExpertId: expert.industryExpertId,
            name: expert.name,
            email: expert.email,
            industry: expert.industry,
        });
        setIsEditing(true);
        setEditId(expert._id);

        if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this expert?")) {
            try {
                await axios.delete(`${API_BASE_URL}/api/expert/${id}`);
                toast.success("Expert deleted successfully");
                fetchExperts(currentPage);
            } catch (error) {
                toast.error("Failed to delete expert");
                console.error("Error deleting expert:", error);
            }
        }
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const sheet = workbook.Sheets[sheetName];

                    const rawExperts = XLSX.utils.sheet_to_json(sheet);

                    const cleanedExperts = rawExperts.map(expert => {
                        const cleanExpert = {};

                        Object.keys(expert).forEach(key => {
                            const cleanKey = key.trim();
                            let modelKey;
                            if (cleanKey.toLowerCase().includes('id')) modelKey = 'industryExpertId';
                            else if (cleanKey.toLowerCase().includes('name')) modelKey = 'name';
                            else if (cleanKey.toLowerCase().includes('email')) modelKey = 'email';
                            else if (cleanKey.toLowerCase().includes('industry')) modelKey = 'industry';
                            else return;

                            let value = expert[key];
                            if (typeof value === 'string') value = value.trim().replace(/\r\n/g, ' ');
                            if (modelKey === 'email' && typeof value === 'number') value = value.toString();
                            if (modelKey === 'industryExpertId' && typeof value === 'number') value = value.toString();

                            cleanExpert[modelKey] = value;
                        });
                        return cleanExpert;
                    });

                    setUploadedExperts(cleanedExperts);
                    setUploadError(null);
                } catch (error) {
                    console.error("Error processing Excel file:", error);
                    setUploadError("Failed to process Excel file. Please check the format.");
                    setUploadedExperts(null);
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleSubmitExperts = async () => {
        if (!uploadedExperts || uploadedExperts.length === 0) {
            setUploadError("No experts to submit");
            return;
        }

        try {
            const response = await axios.post(`${API_BASE_URL}/api/experts/bulk`, {
                experts: uploadedExperts
            });

            if (response.status === 201 || response.status === 207) {
                toast.success(`Successfully added ${response.data.count} industry experts`);
                setUploadedExperts(null);
                fetchExperts(currentPage);
            }
        } catch (error) {
            console.error("Error submitting experts:", error);
            const errorMessage = error.response?.data?.message || "Failed to submit experts";
            toast.error(errorMessage);
            setUploadError(errorMessage);
        }
    };

    return (
        <div className="min-h-screen p-4 md:p-8 font-sans">
            <ToastContainer position="top-right" autoClose={3000} />

            <div className="max-w-6xl mx-auto space-y-8">
                {/* Header */}
                <div className="flex items-center gap-3 border-b-2 border-gray-200 pb-4">
                    <div className="bg-[#B7202E]/10 p-3 rounded-full">
                        <FaUserTie className="text-2xl text-[#B7202E]" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Industry Expert Management</h1>
                        <p className="text-gray-500 text-sm">Add and manage industry experts for project guidance</p>
                    </div>
                </div>

                {/* Bulk Upload Section */}
                <div>
                    <h2 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2">
                        <FaCloudUploadAlt className="text-[#B7202E]" /> Bulk Upload Experts
                    </h2>

                    <div className="p-6 rounded-lg border-2 border-dashed border-gray-300">
                        <label className="block text-sm font-medium text-gray-700 mb-4">
                            Select Excel File (.xlsx, .xls, .csv)
                        </label>

                        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                            <div className="relative flex-1 w-full">
                                <input
                                    type="file"
                                    accept=".xlsx, .xls, .csv"
                                    onChange={handleFileUpload}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#B7202E]/10 file:text-[#B7202E] hover:file:bg-[#B7202E]/20 cursor-pointer border border-gray-200 rounded-lg bg-white"
                                />
                            </div>

                            {uploadedExperts && (
                                <button
                                    type="button"
                                    onClick={handleSubmitExperts}
                                    className="bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-sm hover:bg-green-700 transition-all flex items-center gap-2"
                                >
                                    <FaFileExcel />
                                    Submit {uploadedExperts.length} Experts
                                </button>
                            )}
                        </div>

                        {uploadError && (
                            <div className="mt-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-100">
                                {uploadError}
                            </div>
                        )}

                        {uploadedExperts && (
                            <div className="mt-3 p-3 bg-green-50 text-green-700 text-sm rounded-lg border border-green-100">
                                <span className="font-bold">Ready to upload:</span> {uploadedExperts.length} expert(s) found in file.
                            </div>
                        )}
                    </div>
                </div>

                {/* Add/Edit Form */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" ref={formRef}>
                    <h2 className="text-lg font-bold text-gray-700 mb-6 flex items-center gap-2">
                        {isEditing ? <FaEdit className="text-[#B7202E]" /> : <FaPlus className="text-[#B7202E]" />}
                        {isEditing ? "Update Industry Expert" : "Add New Expert"}
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Expert ID</label>
                                <input
                                    type="text"
                                    name="industryExpertId"
                                    value={formData.industryExpertId}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-colors"
                                    placeholder="e.g. EXP001"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-colors"
                                    placeholder="Enter full name"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-colors"
                                    placeholder="expert@company.com"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">Industry / Company</label>
                                <input
                                    type="text"
                                    name="industry"
                                    value={formData.industry}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#B7202E]/20 focus:border-[#B7202E] transition-colors"
                                    placeholder="e.g. Google, Microsoft, etc."
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 pt-4">
                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-5 py-2.5 rounded-lg text-gray-600 bg-gray-100 hover:bg-gray-200 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="submit"
                                className={`px-6 py-2.5 text-white rounded-lg font-bold shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${isEditing
                                    ? "bg-amber-500 hover:bg-amber-600 focus:ring-amber-500"
                                    : "bg-[#B7202E] hover:bg-[#961925] focus:ring-[#B7202E]"
                                    }`}
                            >
                                {isEditing ? "Update Expert" : "Add Expert"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Experts List */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
                        <h2 className="text-lg font-bold text-gray-700">Registered Experts</h2>
                        <div className="text-sm text-gray-500">
                            Total Experts: <span className="font-bold text-[#B7202E]">{totalExperts}</span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-100">
                                    <th className="p-4 font-semibold text-gray-600 text-sm">Expert ID</th>
                                    <th className="p-4 font-semibold text-gray-600 text-sm">Name</th>
                                    <th className="p-4 font-semibold text-gray-600 text-sm">Email</th>
                                    <th className="p-4 font-semibold text-gray-600 text-sm">Industry</th>
                                    <th className="p-4 font-semibold text-gray-600 text-sm text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {experts.length > 0 ? (
                                    experts.map((expert) => (
                                        <tr key={expert._id} className="hover:bg-gray-50 transition-colors">
                                            <td className="p-4 text-sm font-medium text-gray-900">{expert.industryExpertId}</td>
                                            <td className="p-4 text-sm text-gray-700">{expert.name}</td>
                                            <td className="p-4 text-sm text-gray-500">{expert.email}</td>
                                            <td className="p-4 text-sm text-gray-700">
                                                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium border border-blue-100">
                                                    {expert.industry}
                                                </span>
                                            </td>
                                            <td className="p-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => handleEdit(expert)}
                                                        className="p-2 text-amber-500 hover:bg-amber-50 rounded-lg transition-colors"
                                                        title="Edit"
                                                    >
                                                        <FaEdit />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(expert._id)}
                                                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete"
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-gray-400 italic">
                                            No experts found. Please add some experts above.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                            >
                                Previous
                            </button>
                            <span className="text-sm text-gray-600 font-medium">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddExpertContainer;
